import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StructuredData from '@/components/base/StructuredData';

const termsSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms & Conditions – 4K Dream',
    url: 'https://www.4k-dream.com/terms',
    description: 'Terms and Conditions for 4K Dream independent streaming service. Read our usage policy, subscription terms, and refund conditions.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.4k-dream.com/' },
        { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: 'https://www.4k-dream.com/terms' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: '4K Dream',
      url: 'https://www.4k-dream.com',
    },
  },
];

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <StructuredData schemas={termsSchemas} id="terms" />
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              alt="4K Dream Logo"
              className="h-9 w-auto object-contain"
              src="https://static.readdy.ai/image/5c7545dbb6198b28906c423b0244c4c9/0789dc6d730dd15b6f1e8e24be7c3580.webp"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1a0a2e] via-[#0d0d1a] to-[#0a0a0f] py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#862ce0]/20 border border-[#862ce0]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-file-text-line text-[#b47ef3] text-sm"></i>
            <span className="text-[#b47ef3] text-sm font-medium">Legal Document</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Terms &amp; Conditions</h1>
          <p className="text-white/60 text-lg">Last updated: April 3, 2026</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="space-y-10">

          {/* Intro */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 flex items-start gap-4">
            <i className="ri-information-line text-amber-400 text-xl flex-shrink-0 mt-0.5"></i>
            <p className="text-white/70 text-sm leading-relaxed">
              Please read these Terms &amp; Conditions carefully before using the 4K Dream service. By accessing or using our service, you agree to be bound by these terms. If you do not agree to any part of these terms, you may not access the service.
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">1</span>
              Acceptance of Terms
            </h2>
            <p className="text-white/70 leading-relaxed">
              By accessing and using the 4K Dream IPTV streaming service ("Service"), you accept and agree to be bound by these Terms &amp; Conditions and our Privacy Policy. These terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">2</span>
              Description of Service
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              4K Dream provides an IPTV streaming service that offers access to live TV channels, movies, series, and on-demand content from around the world. Our service includes:
            </p>
            <ul className="space-y-3 text-white/70">
              {[
                'Access to +25,000 live TV channels in HD and 4K quality',
                'On-demand library of +150,000 movies and series',
                'Multi-device support (Smart TV, Android, iOS, PC, MAG, etc.)',
                'Electronic Program Guide (EPG)',
                '24/7 technical support via WhatsApp and email',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-check-line text-[#b47ef3] mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">3</span>
              Subscriptions & Payments
            </h2>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Subscription Plans</h3>
                <p className="text-white/60 text-sm leading-relaxed">We offer various subscription plans (1 month, 3 months, 6 months, 12 months). Prices are clearly displayed on our website and are subject to change with prior notice.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Payment</h3>
                <p className="text-white/60 text-sm leading-relaxed">Payment is due at the time of purchase. We accept major credit cards, PayPal, and other payment methods as displayed at checkout. All transactions are processed securely.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Activation</h3>
                <p className="text-white/60 text-sm leading-relaxed">Your subscription will be activated within 5 minutes of confirmed payment. You will receive your login credentials via email or WhatsApp.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Free Trial</h3>
                <p className="text-white/60 text-sm leading-relaxed">We offer a 24-hour free trial for new users. The trial is limited to one per household/IP address. No payment information is required for the trial.</p>
              </div>
            </div>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">4</span>
              Refund Policy
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We offer a <strong className="text-white">7-day money-back guarantee</strong> on all subscription plans, subject to the following conditions:
            </p>
            <ul className="space-y-3 text-white/70">
              {[
                'Refund requests must be submitted within 7 days of the original purchase date',
                'The service must not have been used excessively (more than 2 hours of total viewing)',
                'Refunds are not available for free trial conversions or promotional offers',
                'Refunds will be processed to the original payment method within 5-10 business days',
                'Chargebacks initiated without contacting our support team first may result in account suspension',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#b47ef3] mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">5</span>
              Acceptable Use Policy
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">You agree to use the Service only for lawful purposes. You are prohibited from:</p>
            <div className="space-y-3">
              {[
                { icon: 'ri-share-line', text: 'Sharing your account credentials with others outside your household' },
                { icon: 'ri-record-circle-line', text: 'Recording, redistributing, or re-broadcasting any content from the Service' },
                { icon: 'ri-bug-line', text: 'Attempting to hack, reverse-engineer, or disrupt the Service' },
                { icon: 'ri-user-add-line', text: 'Creating multiple accounts to abuse free trial offers' },
                { icon: 'ri-global-line', text: 'Using VPNs or proxies to circumvent geographic restrictions in violation of content licenses' },
                { icon: 'ri-spam-2-line', text: 'Using the Service for any commercial purpose without our written consent' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3 bg-red-500/5 border border-red-500/10 rounded-xl p-4">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500/20 flex-shrink-0">
                    <i className={`${item.icon} text-red-400 text-sm`}></i>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">6</span>
              Account Suspension & Termination
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We reserve the right to suspend or terminate your account at our sole discretion, without prior notice, for conduct that we believe:
            </p>
            <ul className="space-y-3 text-white/70">
              {[
                'Violates these Terms & Conditions',
                'Is harmful to other users, us, or third parties',
                'Involves fraudulent activity or chargebacks',
                'Involves sharing or reselling account access',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-white/70 leading-relaxed mt-4">
              In cases of termination due to policy violations, no refund will be issued.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">7</span>
              Service Availability
            </h2>
            <p className="text-white/70 leading-relaxed">
              We strive to provide a reliable, high-quality streaming experience. However, we do not guarantee uninterrupted or error-free service. The Service may be temporarily unavailable due to maintenance, technical issues, or circumstances beyond our control (e.g., internet outages, server maintenance).
            </p>
            <p className="text-white/70 leading-relaxed mt-3">
              We are not liable for any interruptions, delays, or failures in the Service. In cases of extended outages (more than 48 hours), we may offer service extensions at our discretion.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">8</span>
              Intellectual Property
            </h2>
            <p className="text-white/70 leading-relaxed">
              All content available through the Service is protected by copyright and other intellectual property laws. You may not copy, reproduce, distribute, transmit, broadcast, display, sell, license, or otherwise exploit any content for any other purposes without the prior written consent of 4K Dream or the respective rights holders.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">9</span>
              Limitation of Liability
            </h2>
            <p className="text-white/70 leading-relaxed">
              To the maximum extent permitted by applicable law, 4K Dream shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of (or inability to access or use) the Service.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">10</span>
              Governing Law
            </h2>
            <p className="text-white/70 leading-relaxed">
              These Terms shall be governed and construed in accordance with applicable laws, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the competent courts.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">11</span>
              Changes to Terms
            </h2>
            <p className="text-white/70 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by updating the "Last updated" date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the new Terms.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">12</span>
              Contact Us
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              If you have any questions about these Terms &amp; Conditions, please contact us:
            </p>
            <div className="bg-gradient-to-br from-[#862ce0]/20 to-[#c026d3]/10 border border-[#862ce0]/30 rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-mail-line text-[#b47ef3]"></i>
                <a href="mailto:support@4k-dream.com" className="hover:text-[#b47ef3] transition-colors">support@4k-dream.com</a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-phone-line text-[#b47ef3]"></i>
                <span>+1 (205) 709 2376</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-whatsapp-line text-[#b47ef3]"></i>
                <a href="https://wa.me/12057092376" target="_blank" rel="noopener noreferrer" className="hover:text-[#b47ef3] transition-colors">WhatsApp Support</a>
              </div>
            </div>
          </section>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <Link to="/privacy" className="inline-flex items-center gap-2 text-[#b47ef3] hover:text-white transition-colors cursor-pointer text-sm">
            View Privacy Policy
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/10 py-6 px-4 text-center text-white/40 text-sm">
        <p>4K Dream 2019-2026 © All Rights Reserved &nbsp;·&nbsp;
          <Link to="/privacy" className="hover:text-[#b47ef3] transition-colors">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms" className="hover:text-[#b47ef3] transition-colors">Terms &amp; Conditions</Link>
        </p>
      </footer>
    </div>
  );
};

export default TermsConditions;
